import React, { useState, useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import logger from '../../utils/logger';
import { 
  User, 
  Bot, 
  Copy, 
  Check, 
  ExternalLink, 
  FileText, 
  Image, 
  Download,
  AlertCircle,
  CheckCircle,
  Info,
  TrendingUp,
  Users,
  Calendar,
  Mail,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Minimize2,
  Edit3,
  Save,
  X as XIcon
} from 'lucide-react';

const ChatMessage = ({ message, isUser, onEditMessage }) => {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [animatedText, setAnimatedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [showFullContent, setShowFullContent] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(message.content);
  const animationRef = useRef(null);
  const contentRef = useRef(null);
  const editTextareaRef = useRef(null);
  
  // Check if content is long enough to need expansion
  const isLongContent = message.content && message.content.length > 200;
  const shouldTruncate = isLongContent && !showFullContent;
  const displayContent = shouldTruncate ? message.content.substring(0, 200) + '...' : message.content;
  
  // Toggle content expansion
  const toggleExpansion = () => {
    setShowFullContent(!showFullContent);
  };
  
  // Handle edit message
  const handleEditMessage = () => {
    setIsEditing(true);
    setEditContent(message.content);
    setTimeout(() => {
      if (editTextareaRef.current) {
        editTextareaRef.current.focus();
        editTextareaRef.current.setSelectionRange(editTextareaRef.current.value.length, editTextareaRef.current.value.length);
      }
    }, 100);
  };
  
  // Handle save edited message
  const handleSaveEdit = () => {
    if (editContent.trim() && editContent !== message.content && onEditMessage) {
      onEditMessage(message.id, editContent.trim());
    }
    setIsEditing(false);
  };
  
  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent(message.content);
  };
  
  // Handle edit textarea key press
  const handleEditKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };
  
  // Animated text rendering for agent messages
  useEffect(() => {
    if (!isUser && message.content && !message.metadata?.isWelcome) {
      setIsAnimating(true);
      setAnimatedText('');
      
      const words = message.content.split(' ');
      let currentIndex = 0;
      
      const animateText = () => {
        if (currentIndex < words.length) {
          setAnimatedText(prev => {
            const newText = prev + (prev ? ' ' : '') + words[currentIndex];
            currentIndex++;
            return newText;
          });
          
          animationRef.current = setTimeout(animateText, 50); // 50ms delay between words
        } else {
          setIsAnimating(false);
        }
      };
      
      // Start animation after a brief delay
      const startDelay = setTimeout(animateText, 300);
      
      return () => {
        clearTimeout(startDelay);
        if (animationRef.current) {
          clearTimeout(animationRef.current);
        }
      };
    } else {
      setAnimatedText(message.content);
    }
  }, [message.content, isUser, message.metadata?.isWelcome]);

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logger.error('Failed to copy:', error);
    }
  };

  // Format message content with markdown-like formatting
  const formatContent = (content) => {
    if (!content) return '';
    
    // Handle line breaks
    let formatted = content.replace(/\n/g, '<br />');
    
    // Handle bold text
    formatted = formatted.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    
    // Handle italic text
    formatted = formatted.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Handle code blocks
    formatted = formatted.replace(/```([\s\S]*?)```/g, '<pre class="bg-gray-100 p-2 rounded text-sm overflow-x-auto"><code>$1</code></pre>');
    
    // Handle inline code
    formatted = formatted.replace(/`([^`]+)`/g, '<code class="bg-gray-100 px-1 rounded text-sm">$1</code>');
    
    // Handle bullet points
    formatted = formatted.replace(/^• (.+)$/gm, '<li class="ml-4">$1</li>');
    formatted = formatted.replace(/(<li[^>]*>.*<\/li>)/s, '<ul class="list-disc list-inside space-y-1">$1</ul>');
    
    // SECURITY: Sanitize HTML to prevent XSS attacks
    return DOMPurify.sanitize(formatted, {
      ALLOWED_TAGS: ['br', 'strong', 'em', 'pre', 'code', 'li', 'ul', 'ol', 'a', 'span', 'div', 'p'],
      ALLOWED_ATTR: ['class', 'href', 'target', 'rel'],
      ALLOW_DATA_ATTR: false
    });
  };

  // Render data visualization for structured data
  const renderDataVisualization = (data) => {
    if (!data || typeof data !== 'object') return null;

    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center space-x-2 mb-2">
          <TrendingUp className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-gray-700">Data Insights</span>
        </div>
        
        <div className="grid grid-cols-2 gap-3 text-sm">
          {Object.entries(data).map(([key, value]) => (
            <div key={key} className="flex justify-between">
              <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}:</span>
              <span className="font-medium text-gray-900">{value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render action buttons
  const renderActions = (actions) => {
    if (!actions || actions.length === 0) return null;

    return (
      <div className="mt-3 flex flex-wrap gap-2">
        {actions.map((action, index) => (
          <button
            key={index}
            onClick={() => handleActionClick(action)}
            className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200 transition-colors flex items-center space-x-1"
          >
            {getActionIcon(action.type)}
            <span>{action.label}</span>
          </button>
        ))}
      </div>
    );
  };

  // Handle action button clicks
  const handleActionClick = (action) => {
    switch (action.type) {
      case 'navigate':
        if (action.path) {
          window.location.hash = action.path;
        }
        break;
      case 'copy':
        if (action.data) {
          navigator.clipboard.writeText(JSON.stringify(action.data, null, 2));
        }
        break;
      case 'download':
        if (action.data) {
          const blob = new Blob([JSON.stringify(action.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = action.filename || 'data.json';
          a.click();
          URL.revokeObjectURL(url);
        }
        break;
      default:
        logger.log('Action clicked:', action);
    }
    // Add haptic feedback if available
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  // Get icon for action type
  const getActionIcon = (type) => {
    const iconMap = {
      navigate: <ExternalLink className="w-4 h-4" />,
      copy: <Copy className="w-4 h-4" />,
      download: <Download className="w-4 h-4" />,
      email: <Mail className="w-4 h-4" />,
      calendar: <Calendar className="w-4 h-4" />,
      users: <Users className="w-4 h-4" />,
      trending: <TrendingUp className="w-4 h-4" />
    };
    return iconMap[type] || <Info className="w-4 h-4" />;
  };

  // Render file attachments
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) return null;

    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-4 space-y-2"
      >
        {attachments.map((attachment, index) => (
          <motion.div 
            key={index}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
            className="flex items-center space-x-3 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 hover:shadow-md transition-all"
          >
            <div className="p-2 bg-white rounded-lg shadow-sm">
              {attachment.type?.startsWith('image/') ? (
                <Image className="w-4 h-4 text-blue-600" />
              ) : (
                <FileText className="w-4 h-4 text-green-600" />
              )}
            </div>
            <span className="text-sm text-gray-700 flex-1 font-medium">{attachment.name}</span>
            <button className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-all">
              <Download className="w-4 h-4" />
            </button>
          </motion.div>
        ))}
      </motion.div>
    );
  };

  // Get message status icon
  const getStatusIcon = () => {
    if (message.message_type === 'error') {
      return <AlertCircle className="w-4 h-4 text-red-500" />;
    }
    if (message.metadata?.confidence && message.metadata.confidence > 0.9) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return null;
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -20, scale: 0.95 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={`group flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
    >
      <div className={`flex max-w-[85%] ${isUser ? 'flex-row-reverse' : 'flex-row'} items-end space-x-2`}>
        {/* Avatar */}
        <motion.div 
          className={`flex-shrink-0 ${isUser ? 'ml-2' : 'mr-2'}`}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
        >
          <div className={`w-8 h-8 rounded-full flex items-center justify-center shadow-sm ${
            isUser 
              ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white' 
              : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-700 border border-gray-300'
          }`}>
            {isUser ? (
              <User className="w-4 h-4" />
            ) : (
              <span className="text-xs font-bold">BG</span>
            )}
          </div>
        </motion.div>

        {/* Message Content */}
        <div className={`flex-1 ${isUser ? 'text-right' : 'text-left'}`}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className={`relative inline-block max-w-full ${
              expanded ? 'w-full' : ''
            }`}
          >
            <div
              className={`p-3 rounded-2xl shadow-sm transition-all duration-300 hover:shadow-md ${
                isUser
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white rounded-br-md'
                  : message.message_type === 'error'
                  ? 'bg-gradient-to-br from-red-50 to-red-100 text-red-800 border border-red-200 rounded-bl-md'
                  : 'bg-gradient-to-br from-white to-gray-50 text-gray-800 border border-gray-200 rounded-bl-md shadow-sm'
              } ${expanded ? 'rounded-lg' : ''}`}
            >
              {/* Message Content */}
              <div className="relative">
                {isEditing ? (
                  /* Edit Mode */
                  <div className="space-y-2">
                    <textarea
                      ref={editTextareaRef}
                      value={editContent}
                      onChange={(e) => setEditContent(e.target.value)}
                      onKeyDown={handleEditKeyPress}
                      className={`w-full p-2 rounded-lg border resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isUser 
                          ? 'bg-blue-500 text-white border-blue-400 placeholder-blue-200' 
                          : 'bg-white text-gray-900 border-gray-300'
                      }`}
                      rows={3}
                      style={{ minHeight: '60px' }}
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={handleSaveEdit}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                          isUser
                            ? 'bg-blue-400 text-white hover:bg-blue-300'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        <Save className="w-3 h-3" />
                        <span>Save</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={`flex items-center space-x-1 px-3 py-1 rounded-lg text-sm transition-colors ${
                          isUser
                            ? 'bg-blue-400 text-white hover:bg-blue-300'
                            : 'bg-gray-500 text-white hover:bg-gray-600'
                        }`}
                      >
                        <XIcon className="w-3 h-3" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Display Mode */
                  <>
                    <div 
                      ref={contentRef}
                      className={`prose prose-sm max-w-none transition-all duration-300 ${
                        isUser ? 'prose-invert' : ''
                      } ${shouldTruncate ? 'overflow-hidden' : ''}`}
                      dangerouslySetInnerHTML={{ 
                        __html: formatContent(
                          isAnimating && !isUser ? animatedText : displayContent
                        ) 
                      }}
                    />
                    
                    {/* Show edited indicator */}
                    {message.edited && (
                      <div className={`mt-1 text-xs opacity-70 ${
                        isUser ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        (edited)
                      </div>
                    )}
                    
                    {/* Typing cursor for animation */}
                    {isAnimating && !isUser && (
                      <motion.span
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.8, repeat: Infinity }}
                        className="inline-block w-0.5 h-4 bg-gray-600 ml-1"
                      />
                    )}
                    
                    {/* Expand/Collapse button for long content */}
                    {isLongContent && (
                      <button
                        onClick={toggleExpansion}
                        className={`mt-2 flex items-center space-x-1 text-sm transition-colors ${
                          isUser 
                            ? 'text-blue-100 hover:text-white' 
                            : 'text-blue-600 hover:text-blue-800'
                        }`}
                      >
                        {showFullContent ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            <span>Show less</span>
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />
                            <span>Show more</span>
                          </>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Attachments */}
              {renderAttachments(message.attachments)}

              {/* Data Visualization */}
              {!isUser && message.metadata?.data && renderDataVisualization(message.metadata.data)}

              {/* Action Buttons */}
              {!isUser && message.metadata?.actions && renderActions(message.metadata.actions)}
            </div>
            
            {/* Expand/Minimize button */}
            <button
              onClick={() => setExpanded(!expanded)}
              className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-full ${
                isUser 
                  ? 'text-blue-100 hover:text-white hover:bg-blue-500' 
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              }`}
              title={expanded ? 'Minimize' : 'Expand'}
            >
              {expanded ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            </button>
          </motion.div>

          {/* Message Footer */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className={`flex items-center mt-1 space-x-2 text-xs text-gray-500 ${
              isUser ? 'justify-end' : 'justify-start'
            }`}
          >
            <span className="font-medium">{formatTime(message.created_at)}</span>
            {getStatusIcon()}
            
            {/* Edit Button (for user messages only) */}
            {isUser && onEditMessage && !isEditing && (
              <button
                onClick={handleEditMessage}
                className="opacity-0 group-hover:opacity-100 hover:text-gray-700 transition-all p-1 rounded hover:bg-gray-100"
                title="Edit message"
              >
                <Edit3 className="w-3 h-3" />
              </button>
            )}
            
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="opacity-0 group-hover:opacity-100 hover:text-gray-700 transition-all p-1 rounded hover:bg-gray-100"
              title="Copy message"
            >
              {copied ? <Check className="w-3 h-3 text-green-600" /> : <Copy className="w-3 h-3" />}
            </button>
          </motion.div>

          {/* Confidence Score (for agent messages) */}
          {!isUser && message.metadata?.confidence && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-1 text-xs text-gray-400 flex items-center space-x-1"
            >
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Confidence: {Math.round(message.metadata.confidence * 100)}%</span>
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage;